import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddBusinessRoundedIcon from '@mui/icons-material/AddBusinessRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import AdminLayout, { AdminSection, AdminStatCard } from '../components/AdminLayout';
import axiosInstance from '../utils/axiosInstance';
import { getBusinessRoleLabel } from '../utils/userProfile';

const emptyAddress = {
  line1: '',
  line2: '',
  city: '',
  region: '',
  postal_code: '',
  country: 'US',
};

const emptyBusinessUnitForm = {
  partner_code: '',
  display_name: '',
  legal_name: '',
  contact_email: '',
  contact_phone: '',
  status: 'active',
  external_sharing_enabled: false,
  same_as_parent_address: true,
  physical_main_address: '',
  physical_address: { ...emptyAddress },
  logo_file_id: '',
};

const emptyInviteForm = {
  email: '',
  full_name: '',
  role: 'partner_user',
};

const emptyAccessForm = {
  business_unit_id: '',
  cognito_sub: '',
};

function formatDate(value) {
  if (!value) {
    return '-';
  }
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(value));
  } catch {
    return String(value);
  }
}

function statusColor(status) {
  if (status === 'active' || status === 'available' || status === 'normal') {
    return 'success';
  }
  if (status === 'suspended' || status === 'critical') {
    return 'error';
  }
  if (status === 'inactive' || status === 'high' || status === 'medium') {
    return 'warning';
  }
  return 'default';
}

function displayName(value) {
  return value?.full_name || value?.email || value?.cognito_sub || '-';
}

function partnerName(value) {
  return value?.display_name || value?.partner_code || value?.partner_id || '-';
}

function cloneAddress(address = {}) {
  return {
    ...emptyAddress,
    ...address,
    region: address.region || address.state || '',
    postal_code: address.postal_code || address.zipcode || address.zip || '',
    country: address.country || 'US',
  };
}

function normalizeAddress(value, fallbackText = '') {
  const source = value && typeof value === 'object' ? value : {};
  const address = cloneAddress(source);
  if (!address.line1 && fallbackText) {
    address.line1 = fallbackText;
  }
  return address;
}

function formatAddress(address) {
  const parts = [
    address?.line1,
    address?.line2,
    address?.city,
    address?.region,
    address?.postal_code,
    address?.country,
  ].map((value) => String(value || '').trim()).filter(Boolean);
  return parts.join(', ');
}

function addressPayload(address) {
  const payload = cloneAddress(address);
  const formatted = formatAddress(payload);
  if (formatted) {
    payload.formatted = formatted;
  }
  return payload;
}

function businessUnitFormFromUnit(unit) {
  const metadata = unit?.metadata && typeof unit.metadata === 'object' ? unit.metadata : {};
  const physicalAddress = normalizeAddress(unit?.physical_address, unit?.physical_main_address || '');
  return {
    partner_code: unit?.partner_code || '',
    display_name: unit?.display_name || '',
    legal_name: unit?.legal_name || '',
    contact_email: unit?.contact_email || '',
    contact_phone: unit?.contact_phone || '',
    status: unit?.status || 'active',
    external_sharing_enabled: Boolean(unit?.external_sharing_enabled),
    same_as_parent_address: Boolean(metadata.same_as_parent_address),
    physical_main_address: unit?.physical_main_address || formatAddress(physicalAddress),
    physical_address: physicalAddress,
    logo_file_id: unit?.logo_file_id || '',
  };
}

function freshBusinessUnitForm() {
  return {
    ...emptyBusinessUnitForm,
    physical_address: { ...emptyAddress },
  };
}

function deactivationReason(unit) {
  if (unit.status !== 'active') {
    return '';
  }
  const summary = unit.management_summary || {};
  const reasons = [];
  if ((summary.active_users || 0) > 0) {
    reasons.push(`${summary.active_users} active user(s)`);
  }
  if ((summary.assets || 0) > 0) {
    reasons.push(`${summary.assets} asset(s)`);
  }
  if (unit.can_deactivate === false && reasons.length === 0) {
    reasons.push('final active business unit');
  }
  return reasons.length ? `Cannot deactivate: ${reasons.join(', ')}` : '';
}

function sharingTargetName(grant) {
  if (grant.target_email) {
    return grant.target_email;
  }
  if (grant.target_user?.full_name || grant.target_user?.email || grant.target_user?.cognito_sub) {
    return displayName(grant.target_user);
  }
  if (grant.target_partner?.display_name || grant.target_partner?.partner_code || grant.target_partner?.partner_id) {
    return partnerName(grant.target_partner);
  }
  return '-';
}

export default function PartnerAdminDashboard(props) {
  const [state, setState] = useState({ loading: true, error: '', data: null });
  const [feedback, setFeedback] = useState('');
  const [actionError, setActionError] = useState('');
  const [businessUnitForm, setBusinessUnitForm] = useState(freshBusinessUnitForm);
  const [editingBusinessUnitId, setEditingBusinessUnitId] = useState('');
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [inviteForm, setInviteForm] = useState(emptyInviteForm);
  const [accessForm, setAccessForm] = useState(emptyAccessForm);
  const [settingsDraft, setSettingsDraft] = useState(null);
  const [savingKey, setSavingKey] = useState('');
  const [uploadingBusinessUnitLogo, setUploadingBusinessUnitLogo] = useState(false);
  const [filters, setFilters] = useState({
    user_id: '',
    thread: '',
    cui: '',
    criticality: '',
    external_partner_id: '',
  });

  async function loadDashboard(nextFilters = filters) {
    setState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const response = await axiosInstance.get('/partner-admin/dashboard', { params: nextFilters });
      const data = response.data || {};
      setState({ loading: false, error: '', data });
      setSettingsDraft({
        partner_id: data.partner?.partner_id || '',
        external_sharing_enabled: Boolean(data.partner?.external_sharing_enabled),
        child_external_sharing_enabled: Boolean(data.partner?.child_external_sharing_enabled),
      });
      const activeUnits = Array.isArray(data.business_units)
        ? data.business_units.filter((unit) => unit.status === 'active')
        : [];
      setAccessForm((current) => ({
        ...current,
        business_unit_id: activeUnits.some((unit) => unit.partner_id === current.business_unit_id)
          ? current.business_unit_id
          : activeUnits[0]?.partner_id || '',
      }));
    } catch (error) {
      setState({
        loading: false,
        error: error?.response?.data?.message || 'Unable to load partner admin dashboard.',
        data: null,
      });
    }
  }

  useEffect(() => {
    loadDashboard();
  }, []);

  const data = state.data || {};
  const summary = data.summary || {};
  const adminScope = data.admin_scope || {};
  const hasAdminScope = Object.keys(adminScope).length > 0;
  const canManageBusinessUnits = hasAdminScope ? Boolean(adminScope.can_manage_business_units) : true;
  const canManagePartnerSettings = hasAdminScope ? Boolean(adminScope.can_manage_partner_settings) : true;
  const canManageParentUsers = hasAdminScope ? Boolean(adminScope.can_manage_parent_users) : true;
  const users = Array.isArray(data.users) ? data.users : [];
  const businessUnits = Array.isArray(data.business_units) ? data.business_units : [];
  const activeBusinessUnits = businessUnits.filter((unit) => unit.status === 'active');
  const parentPartnerUsers = users.filter((user) => (
    user.membership?.partner_type === 'parent'
    && ['active', 'pending_login', 'invited'].includes(user.status)
  ));
  const existingBusinessUnitAccess = new Set(
    users
      .filter((user) => user.membership?.partner_type === 'child')
      .map((user) => `${user.membership?.partner_id}:${user.cognito_sub}`),
  );
  const accessUserOptions = parentPartnerUsers.filter((user) => (
    !existingBusinessUnitAccess.has(`${accessForm.business_unit_id}:${user.cognito_sub}`)
  ));
  const sharingGrants = Array.isArray(data.sharing_grants) ? data.sharing_grants : [];
  const externalPartnerOptions = useMemo(() => {
    const partners = new Map();
    sharingGrants.forEach((grant) => {
      if (grant.target_partner?.partner_id && grant.is_external) {
        partners.set(grant.target_partner.partner_id, grant.target_partner);
      }
    });
    return Array.from(partners.values());
  }, [sharingGrants]);

  const statCards = [
    { label: 'Business Units', value: summary.business_units || 0 },
    { label: 'Users', value: summary.users || 0, detail: `${summary.active_users || 0} active` },
    { label: 'Workspaces', value: summary.workspaces || 0 },
    { label: 'Threads', value: summary.threads || 0 },
    { label: 'Exchange Cards', value: summary.exchange_cards || 0 },
    { label: 'xTDPs', value: summary.tdps || 0 },
    { label: 'External Shares', value: summary.external_shares || 0, detail: `${summary.active_shares || 0} active shares` },
    { label: 'Inactive/Suspended', value: summary.inactive_or_suspended_users || 0 },
  ];

  async function saveSettings(event) {
    event.preventDefault();
    setSavingKey('settings');
    setFeedback('');
    setActionError('');
    try {
      await axiosInstance.patch('/partner-admin/settings', settingsDraft);
      setFeedback('Partner sharing settings updated.');
      await loadDashboard();
    } catch (error) {
      setActionError(error?.response?.data?.message || 'Unable to update partner sharing settings.');
    } finally {
      setSavingKey('');
    }
  }

  function updateBusinessUnitForm(field, value) {
    setBusinessUnitForm((current) => ({ ...current, [field]: value }));
  }

  function updateBusinessUnitAddress(field, value) {
    setBusinessUnitForm((current) => {
      const physicalAddress = { ...current.physical_address, [field]: value };
      return {
        ...current,
        physical_address: physicalAddress,
        physical_main_address: formatAddress(physicalAddress),
      };
    });
  }

  function resetBusinessUnitForm() {
    setBusinessUnitForm(freshBusinessUnitForm());
    setEditingBusinessUnitId('');
  }

  function editBusinessUnit(unit) {
    setBusinessUnitForm(businessUnitFormFromUnit(unit));
    setEditingBusinessUnitId(unit.partner_id);
    setFeedback('');
    setActionError('');
  }

  async function uploadBusinessUnitLogo(file) {
    if (!data.partner?.partner_id || !file) {
      return;
    }
    if (!file.type?.startsWith('image/')) {
      setActionError('Logo must be an image file.');
      return;
    }

    setUploadingBusinessUnitLogo(true);
    setFeedback('');
    setActionError('');
    try {
      const uploadResponse = await axiosInstance.post('/capability-media/upload', {
        partner_id: data.partner.partner_id,
        filename: file.name,
        content_type: file.type,
        size_bytes: file.size,
        target_kind: 'partner_logo',
      });
      const { url, fields, file_id: fileId } = uploadResponse.data || {};
      if (!url || !fileId) {
        throw new Error('Logo upload did not return a target URL.');
      }

      const formData = new FormData();
      Object.entries(fields || {}).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('file', file);

      const s3Response = await fetch(url, {
        method: 'POST',
        body: formData,
      });
      if (!s3Response.ok) {
        throw new Error('Logo upload failed.');
      }
      updateBusinessUnitForm('logo_file_id', fileId);
      setFeedback('Business unit logo uploaded.');
    } catch (error) {
      setActionError(error?.response?.data?.message || error?.message || 'Unable to upload logo.');
    } finally {
      setUploadingBusinessUnitLogo(false);
    }
  }

  async function saveBusinessUnit(event) {
    event.preventDefault();
    setSavingKey('business-unit');
    setFeedback('');
    setActionError('');
    try {
      const physicalAddress = addressPayload(businessUnitForm.physical_address);
      const payload = {
        partner_code: businessUnitForm.partner_code,
        display_name: businessUnitForm.display_name,
        legal_name: businessUnitForm.legal_name || null,
        contact_email: businessUnitForm.contact_email || null,
        contact_phone: businessUnitForm.contact_phone || null,
        status: businessUnitForm.status,
        external_sharing_enabled: Boolean(businessUnitForm.external_sharing_enabled),
        same_as_parent_address: Boolean(businessUnitForm.same_as_parent_address),
        physical_main_address: businessUnitForm.same_as_parent_address ? null : formatAddress(physicalAddress),
        physical_address: businessUnitForm.same_as_parent_address ? null : physicalAddress,
        logo_file_id: businessUnitForm.logo_file_id || null,
      };
      if (editingBusinessUnitId) {
        await axiosInstance.patch(`/partner-admin/business-units/${editingBusinessUnitId}`, payload);
        setFeedback('Business unit updated.');
      } else {
        await axiosInstance.post('/partner-admin/business-units', payload);
        setFeedback('Business unit created.');
      }
      resetBusinessUnitForm();
      await loadDashboard();
    } catch (error) {
      setActionError(error?.response?.data?.message || 'Unable to save business unit.');
    } finally {
      setSavingKey('');
    }
  }

  async function updateBusinessUnit(unit, updates) {
    setSavingKey(`unit:${unit.partner_id}`);
    setFeedback('');
    setActionError('');
    try {
      await axiosInstance.patch(`/partner-admin/business-units/${unit.partner_id}`, updates);
      setFeedback('Business unit updated.');
      await loadDashboard();
    } catch (error) {
      setActionError(error?.response?.data?.message || 'Unable to update business unit.');
    } finally {
      setSavingKey('');
    }
  }

  async function deactivateBusinessUnit(unit) {
    setSavingKey(`unit:${unit.partner_id}`);
    setFeedback('');
    setActionError('');
    try {
      await axiosInstance.delete(`/partner-admin/business-units/${unit.partner_id}`);
      setFeedback('Business unit deactivated.');
      setDeactivateTarget(null);
      await loadDashboard();
    } catch (error) {
      setActionError(error?.response?.data?.message || 'Unable to deactivate business unit.');
    } finally {
      setSavingKey('');
    }
  }

  async function inviteUser(event) {
    event.preventDefault();
    if (!canManageParentUsers || !data.partner?.partner_id) {
      return;
    }
    setSavingKey('invite');
    setFeedback('');
    setActionError('');
    try {
      await axiosInstance.post('/partner-admin/users', {
        email: inviteForm.email,
        full_name: inviteForm.full_name,
        role: inviteForm.role,
      });
      setInviteForm(emptyInviteForm);
      setFeedback('Partner invitation sent. Business unit access can be granted after the user accepts.');
      await loadDashboard();
    } catch (error) {
      setActionError(error?.response?.data?.message || 'Unable to send partner invitation.');
    } finally {
      setSavingKey('');
    }
  }

  async function grantBusinessUnitAccess(event) {
    event.preventDefault();
    if (!accessForm.business_unit_id || !accessForm.cognito_sub) {
      return;
    }
    setSavingKey('access');
    setFeedback('');
    setActionError('');
    try {
      await axiosInstance.post(`/partner-admin/business-units/${accessForm.business_unit_id}/users`, {
        cognito_sub: accessForm.cognito_sub,
        role: 'partner_user',
      });
      setAccessForm((current) => ({ ...current, cognito_sub: '' }));
      setFeedback('Business unit access granted.');
      await loadDashboard();
    } catch (error) {
      setActionError(error?.response?.data?.message || 'Unable to grant business unit access.');
    } finally {
      setSavingKey('');
    }
  }

  async function updateUser(user, updates) {
    const partnerId = user.membership?.partner_id;
    if (!partnerId) {
      return;
    }
    const isParentUser = user.membership?.partner_type === 'parent';
    setSavingKey(`user:${user.cognito_sub}`);
    setFeedback('');
    setActionError('');
    try {
      await axiosInstance.patch(
        isParentUser ? `/partner-admin/users/${user.cognito_sub}` : `/partner-admin/business-units/${partnerId}/users/${user.cognito_sub}`,
        updates,
      );
      setFeedback('User updated.');
      await loadDashboard();
    } catch (error) {
      setActionError(error?.response?.data?.message || 'Unable to update user.');
    } finally {
      setSavingKey('');
    }
  }

  async function removeUser(user) {
    const partnerId = user.membership?.partner_id;
    if (!partnerId) {
      return;
    }
    const isParentUser = user.membership?.partner_type === 'parent';
    setSavingKey(`user:${user.cognito_sub}:remove`);
    setFeedback('');
    setActionError('');
    try {
      await axiosInstance.delete(
        isParentUser ? `/partner-admin/users/${user.cognito_sub}` : `/partner-admin/business-units/${partnerId}/users/${user.cognito_sub}`,
      );
      setFeedback('User removed.');
      await loadDashboard();
    } catch (error) {
      setActionError(error?.response?.data?.message || 'Unable to remove user.');
    } finally {
      setSavingKey('');
    }
  }

  function applyFilters(event) {
    event.preventDefault();
    loadDashboard(filters);
  }

  return (
    <>
      <AdminLayout
      props={props}
      breadcrumbItems={[{ label: 'AMX xConsole', href: '/' }, { label: 'Partner Admin' }]}
      title="Partner Admin"
      subtitle="Business-unit controls, internal account posture, and sharing visibility for your partner organization."
    >
      {state.loading && !data.partner ? (
        <Stack direction="row" spacing={2} alignItems="center">
          <CircularProgress size={24} />
          <Typography>Loading partner administration...</Typography>
        </Stack>
      ) : state.error ? (
        <Alert severity="error">{state.error}</Alert>
      ) : (
        <>
          {feedback ? <Alert severity="success">{feedback}</Alert> : null}
          {actionError ? <Alert severity="error">{actionError}</Alert> : null}

          <Grid container spacing={2}>
            {statCards.map((card) => (
              <Grid key={card.label} size={{ xs: 12, sm: 6, lg: 3 }}>
                <AdminStatCard {...card} />
              </Grid>
            ))}
          </Grid>

          {canManagePartnerSettings ? (
          <AdminSection title="Partner Settings" subtitle="External sharing is controlled at the parent partner and business-unit levels.">
            <Stack component="form" spacing={2} onSubmit={saveSettings}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField label="Parent Partner" value={data.partner?.display_name || ''} disabled fullWidth />
                <TextField label="Partner Code" value={data.partner?.partner_code || ''} disabled fullWidth />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(settingsDraft?.external_sharing_enabled)}
                      onChange={(event) => setSettingsDraft({ ...settingsDraft, external_sharing_enabled: event.target.checked })}
                    />
                  }
                  label="Allow parent-partner external sharing"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(settingsDraft?.child_external_sharing_enabled)}
                      onChange={(event) => setSettingsDraft({ ...settingsDraft, child_external_sharing_enabled: event.target.checked })}
                    />
                  }
                  label="Allow business units to share externally"
                />
              </Stack>
              <Stack direction="row" justifyContent="flex-end">
                <Button type="submit" variant="contained" disabled={savingKey === 'settings'}>
                  {savingKey === 'settings' ? 'Saving...' : 'Save Settings'}
                </Button>
              </Stack>
            </Stack>
          </AdminSection>
          ) : null}

          {canManageBusinessUnits ? (
          <AdminSection title="Business Units" subtitle="Create and administer business units without exposing business-unit identity externally.">
            <Stack spacing={2}>
              <Stack component="form" spacing={2} onSubmit={saveBusinessUnit}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField label="Business Unit Code" value={businessUnitForm.partner_code} onChange={(event) => updateBusinessUnitForm('partner_code', event.target.value)} fullWidth required />
                  <TextField label="Display Name" value={businessUnitForm.display_name} onChange={(event) => updateBusinessUnitForm('display_name', event.target.value)} fullWidth required />
                  <TextField label="Legal Name" value={businessUnitForm.legal_name} onChange={(event) => updateBusinessUnitForm('legal_name', event.target.value)} fullWidth />
                  <TextField select label="Status" value={businessUnitForm.status} onChange={(event) => updateBusinessUnitForm('status', event.target.value)} fullWidth>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                    <MenuItem value="suspended">Suspended</MenuItem>
                  </TextField>
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField label="Contact Email" value={businessUnitForm.contact_email} onChange={(event) => updateBusinessUnitForm('contact_email', event.target.value)} fullWidth />
                  <TextField label="Contact Phone" value={businessUnitForm.contact_phone} onChange={(event) => updateBusinessUnitForm('contact_phone', event.target.value)} fullWidth />
                  <TextField label="Logo File ID" value={businessUnitForm.logo_file_id} fullWidth disabled />
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={uploadingBusinessUnitLogo ? <CircularProgress size={18} /> : <CloudUploadRoundedIcon />}
                    disabled={uploadingBusinessUnitLogo || savingKey === 'business-unit'}
                    sx={{ minWidth: { md: 180 } }}
                  >
                    {uploadingBusinessUnitLogo ? 'Uploading' : 'Upload Logo'}
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        event.target.value = '';
                        uploadBusinessUnitLogo(file);
                      }}
                    />
                  </Button>
                </Stack>
                <FormControlLabel
                  control={
                    <Switch
                      checked={businessUnitForm.same_as_parent_address}
                      onChange={(event) => updateBusinessUnitForm('same_as_parent_address', event.target.checked)}
                    />
                  }
                  label="Use parent partner address"
                />
                {businessUnitForm.same_as_parent_address ? (
                  <Alert severity="info">Address will match {data.partner?.display_name || 'the parent partner'}.</Alert>
                ) : (
                  <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField label="Address Line 1" value={businessUnitForm.physical_address.line1} onChange={(event) => updateBusinessUnitAddress('line1', event.target.value)} fullWidth required />
                      <TextField label="Address Line 2" value={businessUnitForm.physical_address.line2} onChange={(event) => updateBusinessUnitAddress('line2', event.target.value)} fullWidth />
                    </Stack>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField label="City" value={businessUnitForm.physical_address.city} onChange={(event) => updateBusinessUnitAddress('city', event.target.value)} fullWidth required />
                      <TextField label="State" value={businessUnitForm.physical_address.region} onChange={(event) => updateBusinessUnitAddress('region', event.target.value)} fullWidth required />
                      <TextField label="Zip Code" value={businessUnitForm.physical_address.postal_code} onChange={(event) => updateBusinessUnitAddress('postal_code', event.target.value)} fullWidth required />
                    </Stack>
                  </Stack>
                )}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={businessUnitForm.external_sharing_enabled}
                        onChange={(event) => updateBusinessUnitForm('external_sharing_enabled', event.target.checked)}
                      />
                    }
                    label="Business unit may initiate external sharing"
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    {editingBusinessUnitId ? (
                      <Button type="button" variant="text" onClick={resetBusinessUnitForm} disabled={savingKey === 'business-unit'}>
                        Cancel Edit
                      </Button>
                    ) : null}
                    <Button
                      type="submit"
                      variant="outlined"
                      startIcon={editingBusinessUnitId ? <SaveRoundedIcon /> : <AddBusinessRoundedIcon />}
                      disabled={savingKey === 'business-unit' || uploadingBusinessUnitLogo}
                    >
                      {savingKey === 'business-unit'
                        ? 'Saving...'
                        : editingBusinessUnitId
                          ? 'Save Business Unit'
                          : 'Create Business Unit'}
                    </Button>
                  </Stack>
                </Stack>
              </Stack>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Business Unit</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Users</TableCell>
                    <TableCell align="right">Assets</TableCell>
                    <TableCell>External Sharing</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {businessUnits.map((unit) => {
                    const summary = unit.management_summary || {};
                    const blockedReason = deactivationReason(unit);
                    return (
                      <TableRow key={unit.partner_id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{unit.display_name}</Typography>
                          <Typography variant="caption" color="text.secondary">{unit.partner_code}</Typography>
                        </TableCell>
                        <TableCell><Chip size="small" label={unit.status} color={statusColor(unit.status)} /></TableCell>
                        <TableCell align="right">{summary.users || 0}</TableCell>
                        <TableCell align="right">{summary.assets || 0}</TableCell>
                        <TableCell>{unit.external_sharing_enabled ? 'Enabled' : 'Disabled'}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{unit.contact_email || '-'}</Typography>
                          <Typography variant="caption" color="text.secondary">{unit.contact_phone || ''}</Typography>
                        </TableCell>
                        <TableCell>{unit.physical_main_address || unit.physical_address?.formatted || '-'}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditRoundedIcon />}
                              disabled={savingKey === `unit:${unit.partner_id}`}
                              onClick={() => editBusinessUnit(unit)}
                            >
                              Edit
                            </Button>
                            {unit.status === 'active' ? (
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                startIcon={<DeleteOutlineRoundedIcon />}
                                disabled={savingKey === `unit:${unit.partner_id}` || Boolean(blockedReason)}
                                title={blockedReason}
                                onClick={() => setDeactivateTarget(unit)}
                              >
                                Deactivate
                              </Button>
                            ) : (
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={savingKey === `unit:${unit.partner_id}`}
                                onClick={() => updateBusinessUnit(unit, { status: 'active' })}
                              >
                                Activate
                              </Button>
                            )}
                          </Stack>
                          {blockedReason ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              {blockedReason}
                            </Typography>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Stack>
          </AdminSection>
          ) : null}

          <AdminSection title="Users And Account Security" subtitle={`Inactive reminder after ${data.account_security_policy?.inactivity_reminder_days || 30} days; suspension after ${data.account_security_policy?.suspension_after_days || 40} days by current policy.`}>
            <Stack spacing={2}>
              {canManageParentUsers ? (
                <Stack component="form" spacing={2} onSubmit={inviteUser}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>Invite Partner User</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Invitations create access to the parent partner. Business unit access is assigned separately after acceptance.
                    </Typography>
                  </Box>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <TextField
                      label="Email"
                      type="email"
                      value={inviteForm.email}
                      onChange={(event) => setInviteForm({ ...inviteForm, email: event.target.value })}
                      fullWidth
                      required
                    />
                    <TextField
                      label="Full Name"
                      value={inviteForm.full_name}
                      onChange={(event) => setInviteForm({ ...inviteForm, full_name: event.target.value })}
                      fullWidth
                    />
                    <TextField
                      select
                      label="Role"
                      value={inviteForm.role}
                      onChange={(event) => setInviteForm({ ...inviteForm, role: event.target.value })}
                      fullWidth
                    >
                      <MenuItem value="partner_user">User</MenuItem>
                      <MenuItem value="partner_admin">Admin</MenuItem>
                    </TextField>
                  </Stack>
                  <Stack direction="row" justifyContent="flex-end">
                    <Button type="submit" variant="outlined" disabled={savingKey === 'invite'}>
                      {savingKey === 'invite' ? 'Sending...' : 'Send Invitation'}
                    </Button>
                  </Stack>
                </Stack>
              ) : null}

              {canManageBusinessUnits ? (
                <>
                  <Divider />
                  <Stack component="form" spacing={2} onSubmit={grantBusinessUnitAccess}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>Business Unit Access</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Select an existing partner user and grant access to a business unit.
                      </Typography>
                    </Box>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField
                        select
                        label="Business Unit"
                        value={accessForm.business_unit_id}
                        onChange={(event) => setAccessForm({ business_unit_id: event.target.value, cognito_sub: '' })}
                        fullWidth
                        required
                      >
                        {activeBusinessUnits.map((unit) => (
                          <MenuItem key={unit.partner_id} value={unit.partner_id}>
                            {unit.display_name || unit.partner_code}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        select
                        label="Partner User"
                        value={accessForm.cognito_sub}
                        onChange={(event) => setAccessForm({ ...accessForm, cognito_sub: event.target.value })}
                        fullWidth
                        required
                      >
                        {accessUserOptions.map((user) => (
                          <MenuItem key={user.cognito_sub} value={user.cognito_sub}>
                            {displayName(user)} ({user.email})
                          </MenuItem>
                        ))}
                      </TextField>
                    </Stack>
                    <Stack direction="row" justifyContent="flex-end">
                      <Button
                        type="submit"
                        variant="outlined"
                        disabled={savingKey === 'access' || !activeBusinessUnits.length || !accessUserOptions.length}
                      >
                        {savingKey === 'access' ? 'Granting...' : 'Grant Access'}
                      </Button>
                    </Stack>
                  </Stack>
                </>
              ) : null}

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Business Unit</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Account Security</TableCell>
                    <TableCell>Last Activity</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => {
                    const management = user.management || {};
                    const canUpdate = management.can_update !== false;
                    const canDeactivate = management.can_deactivate !== false;
                    const canRemove = management.can_remove !== false;
                    const canChangeRole = Boolean(management.can_change_role);
                    const savingUser = savingKey === `user:${user.cognito_sub}`;
                    const removingUser = savingKey === `user:${user.cognito_sub}:remove`;
                    const protectedReason = management.last_parent_admin ? 'Parent account must keep one parent-level admin.' : '';
                    return (
                      <TableRow key={`${user.membership?.partner_id}-${user.cognito_sub}`} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{displayName(user)}</Typography>
                          <Typography variant="caption" color="text.secondary">{user.email || user.cognito_sub}</Typography>
                        </TableCell>
                        <TableCell>{user.membership?.partner_display_name || '-'}</TableCell>
                        <TableCell><Chip size="small" label={user.status} color={statusColor(user.status)} /></TableCell>
                        <TableCell>
                          <Chip size="small" label={user.account_security?.action || 'none'} color={statusColor(user.account_security?.severity)} variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatDate(user.account_security?.last_activity_at || user.last_activity_at)}</Typography>
                          <Typography variant="caption" color="text.secondary">{user.account_security?.inactive_days || 0} inactive days</Typography>
                        </TableCell>
                        <TableCell>{user.membership?.partner_type === 'child' ? 'Access' : getBusinessRoleLabel(user.membership?.role)}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            {canChangeRole ? (
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={savingUser || !canUpdate}
                                onClick={() => updateUser(user, { role: user.membership?.role === 'partner_admin' ? 'partner_user' : 'partner_admin' })}
                              >
                                {user.membership?.role === 'partner_admin' ? 'Make User' : 'Make Admin'}
                              </Button>
                            ) : null}
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={savingUser || !canUpdate || (user.status === 'active' && !canDeactivate)}
                              title={user.status === 'active' ? protectedReason : ''}
                              onClick={() => updateUser(user, { status: user.status === 'active' ? 'inactive' : 'active' })}
                            >
                              {user.status === 'active' ? 'Deactivate' : 'Activate'}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              startIcon={<DeleteOutlineRoundedIcon />}
                              disabled={removingUser || !canUpdate || !canRemove}
                              title={protectedReason}
                              onClick={() => removeUser(user)}
                            >
                              Remove
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Stack>
          </AdminSection>

          <AdminSection title="Visibility Filters" subtitle="Filter the situational-awareness tables by user, thread, CUI, criticality, or external partner.">
            <Stack component="form" direction={{ xs: 'column', md: 'row' }} spacing={2} onSubmit={applyFilters}>
              <TextField select label="User" value={filters.user_id} onChange={(event) => setFilters({ ...filters, user_id: event.target.value })} fullWidth>
                <MenuItem value="">All users</MenuItem>
                {users.map((user) => (
                  <MenuItem key={`${user.membership?.partner_id}-${user.cognito_sub}`} value={user.cognito_sub}>{displayName(user)}</MenuItem>
                ))}
              </TextField>
              <TextField label="Thread or workspace" value={filters.thread} onChange={(event) => setFilters({ ...filters, thread: event.target.value })} fullWidth />
              <TextField select label="CUI" value={filters.cui} onChange={(event) => setFilters({ ...filters, cui: event.target.value })} fullWidth>
                <MenuItem value="">Any</MenuItem>
                <MenuItem value="true">CUI only</MenuItem>
                <MenuItem value="false">Non-CUI only</MenuItem>
              </TextField>
              <TextField label="Criticality" value={filters.criticality} onChange={(event) => setFilters({ ...filters, criticality: event.target.value })} fullWidth />
              <TextField select label="External partner" value={filters.external_partner_id} onChange={(event) => setFilters({ ...filters, external_partner_id: event.target.value })} fullWidth>
                <MenuItem value="">Any</MenuItem>
                {externalPartnerOptions.map((partner) => (
                  <MenuItem key={partner.partner_id} value={partner.partner_id}>{partnerName(partner)}</MenuItem>
                ))}
              </TextField>
              <Button type="submit" variant="contained">Apply</Button>
            </Stack>
          </AdminSection>

          <AdminSection title="Workspaces And Threads" subtitle="Everything created under the parent partner or its business units.">
            <Stack spacing={2}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Workspace</TableCell>
                    <TableCell>Owner</TableCell>
                    <TableCell>Business Unit</TableCell>
                    <TableCell>CUI</TableCell>
                    <TableCell align="right">Threads</TableCell>
                    <TableCell align="right">External Shares</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.workspaces || []).map((workspace) => (
                    <TableRow key={workspace.workspace_id} hover>
                      <TableCell>{workspace.title}</TableCell>
                      <TableCell>{displayName(workspace.created_by_user)}</TableCell>
                      <TableCell>{partnerName(workspace.partner)}</TableCell>
                      <TableCell>{workspace.is_cui ? 'Yes' : 'No'}</TableCell>
                      <TableCell align="right">{workspace.thread_count}</TableCell>
                      <TableCell align="right">{workspace.external_share_count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Thread</TableCell>
                    <TableCell>Workspace</TableCell>
                    <TableCell>Owner</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Cards</TableCell>
                    <TableCell align="right">External Shares</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.threads || []).map((thread) => (
                    <TableRow key={thread.corr_id} hover>
                      <TableCell>{thread.title}</TableCell>
                      <TableCell>{thread.workspace_title || '-'}</TableCell>
                      <TableCell>{displayName(thread.created_by_user)}</TableCell>
                      <TableCell><Chip size="small" label={thread.status} /></TableCell>
                      <TableCell align="right">{thread.exchange_card_count}</TableCell>
                      <TableCell align="right">{thread.external_share_count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Stack>
          </AdminSection>

          <AdminSection title="Exchange Cards, xTDPs, And Shares" subtitle="Review exchanged artifacts and where they are shared.">
            <Stack spacing={2}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Exchange Card</TableCell>
                    <TableCell>Thread</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Business Unit</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.exchange_cards || []).map((card) => (
                    <TableRow key={card.thread_exchange_card_id} hover>
                      <TableCell>{card.title || card.card_key}</TableCell>
                      <TableCell>{card.thread_title || '-'}</TableCell>
                      <TableCell>{displayName(card.user)}</TableCell>
                      <TableCell>{partnerName(card.partner)}</TableCell>
                      <TableCell><Chip size="small" label={card.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>xTDP</TableCell>
                    <TableCell>Owner</TableCell>
                    <TableCell>Business Unit</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>CUI</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.tdps || []).map((tdp) => (
                    <TableRow key={tdp.tdp_id} hover>
                      <TableCell>{tdp.name}</TableCell>
                      <TableCell>{displayName(tdp.created_by_user)}</TableCell>
                      <TableCell>{partnerName(tdp.partner)}</TableCell>
                      <TableCell><Chip size="small" label={tdp.status} /></TableCell>
                      <TableCell>{tdp.is_cui ? 'Yes' : 'No'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Shared Resource</TableCell>
                    <TableCell>Target</TableCell>
                    <TableCell>Scope</TableCell>
                    <TableCell>Permission</TableCell>
                    <TableCell>CUI</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sharingGrants.map((grant) => (
                    <TableRow key={grant.sharing_grant_id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{grant.resource_title}</Typography>
                        <Typography variant="caption" color="text.secondary">{grant.resource_type}</Typography>
                      </TableCell>
                      <TableCell>{sharingTargetName(grant)}</TableCell>
                      <TableCell><Chip size="small" color={grant.is_external ? 'warning' : 'default'} label={grant.is_external ? 'External' : 'Internal'} /></TableCell>
                      <TableCell>{grant.permission}</TableCell>
                      <TableCell>{grant.is_cui ? 'Yes' : 'No'}</TableCell>
                      <TableCell>{formatDate(grant.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Stack>
          </AdminSection>
        </>
      )}
      </AdminLayout>

      <Dialog open={Boolean(deactivateTarget)} onClose={() => setDeactivateTarget(null)}>
        <DialogTitle>Deactivate Business Unit?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes the business unit from active use without deleting its history. It can be reactivated later.
          </DialogContentText>
          {deactivateTarget ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {deactivateTarget.display_name || deactivateTarget.partner_code}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {deactivateTarget.partner_code}
              </Typography>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateTarget(null)} disabled={Boolean(deactivateTarget && savingKey === `unit:${deactivateTarget.partner_id}`)}>
            Cancel
          </Button>
          <Button
            color="warning"
            variant="contained"
            startIcon={<DeleteOutlineRoundedIcon />}
            disabled={Boolean(deactivateTarget && savingKey === `unit:${deactivateTarget.partner_id}`)}
            onClick={() => deactivateBusinessUnit(deactivateTarget)}
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
